/**
 * acequia.grants — offline-first grants (unified-token-system, decision 8).
 *
 * A grant is complete without the server. It is an anchor-model chain —
 * an inert root anchor (scope, depth) and a presentable leaf (scope, exp,
 * audience, grantee) — minted, verified, renewed and revoked by this browser
 * alone, with its own user's key, registered or not. The server is where a
 * grant is backed up, found by the user's other devices and revoked
 * server-side; it is not where a grant becomes valid. The sync queue that
 * uploads what is minted here is the next step (track 3); every row this
 * module writes is marked `sync: 'pending'` for it.
 *
 *   mint      mintGrant({ scope, aud, grantee, ttlSeconds })
 *             → the anchor and leaf, signed locally, kept in the wallet
 *   present   grantPresentation(anchorHash)
 *             → { token, tokenId, anchors }: the leaf and the anchors it hangs
 *             from, so a verifier that has never seen the grant needs nothing
 *             else (anchors are inert and content-addressed: handing them
 *             over costs nothing, and a forged one fails its hash)
 *   verify    verifyGrant(token, { anchors, aud })
 *             → anchors from the presentation, then the wallet and link cache,
 *             then the network; keys from this browser first; revocation from
 *             the wallet and stored signed revocations. With the auth server
 *             unreachable, a grant this browser minted, or one presented with
 *             its anchors, still verifies.
 *   renew     renewGrant(anchorHash) → a new leaf under the same anchor
 *   revoke    revokeGrant(anchorHash) → the grant marked revoked here, and a
 *             signed revocation to hand to holders and other verifiers
 *
 *   sync      syncGrants() / startGrantSync() → upload what was minted here
 *             once this browser is signed in to an approved account: anchors,
 *             leaves under their client ids (which become their codes), and
 *             revocations of grants already uploaded; and keep the account's
 *             device keys, so its other devices' grants verify offline
 *
 * Grants are self-rooted by default: verifyGrant accepts only chains rooted in
 * this browser's user (the grantor verifying its own grants), as the BrowserDAV
 * engine accepts only its owner's.
 */

import { createId, jose } from '../deps.js'
import { acequiaReady, getUserId, getUserKeys } from '../acequia/acequia.js'
import {
    DEFAULT_RENEWAL_TTL,
    cacheAccountKeys,
    cacheLinks,
    canonicalizeScope,
    createCombinedKeyResolver,
    createCombinedRevocationChecker,
    createLeafForAnchor,
    createLinkResolver,
    createRootAnchor,
    createWalletRevocationChecker,
    getWalletToken,
    hashToken,
    listWalletTokens,
    resolveAndVerify,
    resolveLinkLocally,
    saveWalletRow,
    storeAnchor,
    storeChainToken,
    updateWalletToken,
} from './chains.js'
import { createRevocation, createStoredRevocationChecker, storeRevocation } from './revocations.js'
import { attestAliases, ownIdentityFor, ownUserIds } from './identities.js'

/** The user an anchor is rooted in (its sub). */
function anchorUser(anchorJwt) {
    try {
        return JSON.parse(atob(anchorJwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub || null
    } catch {
        return null
    }
}

const DEFAULT_AUTH_URL = '/auth'

function authUrlOf(authUrl) {
    if (authUrl) return authUrl
    return typeof location !== 'undefined' ? `${location.origin}${DEFAULT_AUTH_URL}` : DEFAULT_AUTH_URL
}

/**
 * Mint a grant with no network: a root anchor and a leaf, both signed with
 * this browser's user key, kept in the wallet.
 *
 * @param {object} opts
 * @param {object} opts.scope - a grant set ({ grants }); legacy shapes are canonicalized
 * @param {string} opts.aud - the app the grant is for ('geocamera', 'debug-console')
 * @param {string} [opts.grantee] - who it is for (a viewer's deviceId)
 * @param {number} [opts.ttlSeconds] - the leaf's lifetime (7 days by default)
 * @param {string} [opts.name] - a label, kept on the anchor
 * @param {string} [opts.note]
 * @param {boolean|null} [opts.autoRenew=null] - the wallet row's renewal policy, read by
 *   the chains engine only for this host's leaves (an app's grant is its manager's to
 *   renew): true always, false never, null (the default) if used
 * @param {object} [opts.claims] - the app's own leaf claims (GeoCamera's `type: 'geo-grant'`),
 *   kept for renewal; they can't override the leaf's own
 * @returns {Promise<{ anchorHash, leafJwt, leafId, expiresAt, anchors: string[] }>}
 */
export async function mintGrant({ scope, aud, grantee = null, ttlSeconds = DEFAULT_RENEWAL_TTL, name, note, autoRenew = null, claims = {} } = {}) {
    if (!scope || typeof scope !== 'object') throw new Error('mintGrant: a scope (grant set) is required')
    if (!aud) throw new Error('mintGrant: an audience (the app the grant is for) is required')
    await acequiaReady()
    const grantSet = canonicalizeScope(scope)

    const anchorJwt = await createRootAnchor(grantSet, { name, note })
    const anchorHash = await hashToken(anchorJwt)
    const leafClaims = { ...claims, ...(grantee ? { grantee } : {}) }
    const leafJwt = await createLeafForAnchor(anchorHash, grantSet, ttlSeconds, { aud, claims: leafClaims })
    const leafHash = await hashToken(leafJwt)
    const leafId = createId()
    const now = new Date()
    const expiresAt = new Date(now.getTime() + ttlSeconds * 1000).toISOString()

    await saveWalletRow(anchorHash, {
        anchorHash,
        rootHash: anchorHash,
        anchorJwt,
        leafJwt,
        leafHash,
        // A client id. It becomes the grant's short code when the grant syncs
        // (decision 8, answer 2); until then only this browser resolves it.
        leafId,
        name: name || null,
        note: note || null,
        scope: grantSet,
        aud,
        grantee,
        leafClaims,
        // An app's grant: kept out of the wallet listings the dashboard and
        // the TokenKeychain read (chains.listWalletTokens)
        kind: 'app-grant',
        origin: 'minted',
        createdAt: now.toISOString(),
        expiresAt,
        status: 'active',
        revokedAt: null,
        autoRenew,
        ttlSeconds,
        // Minted here, not yet on the server: the sync queue's to upload
        sync: 'pending',
    })

    return { anchorHash, leafJwt, leafId, expiresAt, anchors: [anchorJwt] }
}

/** The anchors a grant hangs from, leaf-ward first, from what this browser holds. */
async function anchorsOf(row) {
    const anchors = []
    let jwt = row.anchorJwt || await resolveLinkLocally(row.anchorHash)
    while (jwt && anchors.length < 8) {
        anchors.push(jwt)
        let parent = null
        try {
            parent = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).parent || null
        } catch { /* a malformed anchor ends the walk */ }
        jwt = parent ? await resolveLinkLocally(parent) : null
    }
    return anchors
}

/**
 * What a holder presents: the leaf, its id, and the anchors it hangs from.
 * @param {string} anchorHash
 * @returns {Promise<{ token: string, tokenId: string, anchors: string[] }|null>}
 */
export async function grantPresentation(anchorHash) {
    const row = await getWalletToken(anchorHash)
    if (!row?.leafJwt) return null
    return { token: row.leafJwt, tokenId: row.leafId || null, anchors: await anchorsOf(row) }
}

/** A wallet row by its leaf's client id. */
export async function rowByLeafId(leafId) {
    const rows = await listWalletTokens({ appGrants: true }).catch(() => [])
    return rows.find((r) => r.leafId === leafId) || null
}

/**
 * Verify a presented grant, offline if need be.
 *
 * @param {string} token - the leaf JWT, or its id when this browser minted it
 * @param {object} [opts]
 * @param {string[]} [opts.anchors] - anchors presented with it
 * @param {string} [opts.aud] - the app this grant must be for
 * @param {string|null} [opts.rootedIn] - the user the chain must be rooted in;
 *   by default this browser's user, now or before a device link; null for any; one id or an array
 * @param {string} [opts.authUrl] - the auth server, tried last
 * @returns {Promise<{ valid: boolean, error?: string, expired?: boolean, scope?: object, claims?: object, rootSub?: string, anchorHash?: string }>}
 */
export async function verifyGrant(token, { anchors = [], aud, rootedIn, authUrl } = {}) {
    await acequiaReady()
    let leafJwt = token
    if (typeof token === 'string' && !token.includes('.')) {
        const row = await rowByLeafId(token)
        if (!row) return { valid: false, error: 'Grant not found' }
        leafJwt = row.leafJwt
    }
    if (typeof leafJwt !== 'string') return { valid: false, error: 'No token' }

    // Anchors handed over by the presenter, each under the hash it names: a
    // substituted anchor hashes to something nothing points at, so it is
    // never used, never believed
    const inline = new Map()
    for (const jwt of anchors || []) {
        if (typeof jwt !== 'string') continue
        try { inline.set(await hashToken(jwt), jwt) } catch { /* skip */ }
    }
    const url = authUrlOf(authUrl)
    const resolveLink = async (hash) => inline.get(hash) || resolveLinkLocally(hash)
    const resolvePublicKey = createCombinedKeyResolver(url)
    const stored = createStoredRevocationChecker({ authUrl: url, resolvePublicKey, resolveLink: createLinkResolver(url, { resolveLink }) })
    const isRevoked = createCombinedRevocationChecker(createWalletRevocationChecker(), stored)

    // The walk asks about the anchor and then the leaf: one request for both
    // (on acequia.io two lookups per camera request were 29% of all requests)
    try {
        const anchor = jose.decodeJwt(leafJwt).anchor
        if (anchor) await stored.prefetch?.([anchor, await hashToken(leafJwt)])
    } catch { /* undecodable: the walk below refuses it */ }

    const result = await resolveAndVerify(url, leafJwt, { resolveLink, resolvePublicKey, isRevoked })
    if (!result.valid) {
        // Revoked where this browser didn't see it (the server's list: a
        // revocation from the dashboard or another browser): a grant minted
        // here learns it, so its grant manager stops letting the holder in
        // and stops renewing it (geocamera-and-cameramap-issues-and-fixes, item 6)
        if (result.error === 'Root token revoked') {
            let anchor = null
            try { anchor = jose.decodeJwt(leafJwt).anchor || null } catch { /* undecodable */ }
            if (anchor) await learnRevoked(anchor, 'verify').catch(() => {})
        }
        // A lapsed leaf is its own kind of failure: a holder with auto-renew
        // is renewed, not refused (jose words it as its "exp" claim check)
        const expired = /expir|"exp" claim/i.test(result.error || '')
        return { valid: false, error: expired ? 'Grant expired' : result.error, expired }
    }

    const owners = rootedIn === undefined ? await ownUserIds() : rootedIn ? [].concat(rootedIn) : null
    if (owners && !owners.includes(result.rootSub)) {
        return { valid: false, error: 'Grant is rooted in another user' }
    }
    const claims = result.leafPayload || {}
    if (aud) {
        const list = [].concat(claims.aud ?? [])
        if (!list.includes(aud)) return { valid: false, error: `Grant is for ${list.join(', ') || 'no app'}, not ${aud}` }
    }

    // Keep what was handed over: the next presentation resolves locally
    if (inline.size) await cacheLinks([...inline.values()])

    return {
        valid: true,
        scope: canonicalizeScope(result.effectiveScope),
        claims,
        rootSub: result.rootSub,
        anchorHash: claims.anchor || null,
        revocationCheck: result.revocationCheck,
    }
}

/**
 * Renew a grant this browser minted: a new leaf under the same anchor, with
 * the same scope, audience and grantee. No network.
 * @param {string} anchorHash
 * @param {object} [opts]
 * @param {number} [opts.ttlSeconds] - defaults to the grant's own
 * @returns {Promise<{ token: string, tokenId: string, anchors: string[], expiresAt: string }>}
 */
export async function renewGrant(anchorHash, { ttlSeconds } = {}) {
    const row = await getWalletToken(anchorHash)
    if (!row) throw new Error('renewGrant: no such grant in this wallet')
    if (row.status === 'revoked') throw new Error('renewGrant: the grant is revoked')
    const ttl = ttlSeconds || row.ttlSeconds || DEFAULT_RENEWAL_TTL
    // Signed by the anchor's user — a kept identity, for a grant minted
    // before this browser linked to an account
    const signer = await ownIdentityFor(anchorUser(row.anchorJwt))
    if (!signer?.userKeys) throw new Error('renewGrant: this browser no longer holds the key the grant is rooted in')
    const leafJwt = await createLeafForAnchor(row.anchorHash, row.scope, ttl, {
        aud: row.aud,
        claims: row.leafClaims || (row.grantee ? { grantee: row.grantee } : {}),
        signer,
    })
    const expiresAt = new Date(Date.now() + ttl * 1000).toISOString()
    await updateWalletToken(row.anchorHash, {
        leafJwt,
        leafHash: await hashToken(leafJwt),
        expiresAt,
        renewedAt: new Date().toISOString(),
        // The anchor is unchanged; a synced grant's new leaf still has to go up
        sync: row.sync === 'synced' ? 'leaf-pending' : row.sync,
    })
    return { token: leafJwt, tokenId: row.leafId || null, anchors: await anchorsOf(row), expiresAt }
}

/**
 * Revoke a grant this browser minted: marked revoked in the wallet (so this
 * browser refuses it at once), and a signed revocation of its anchor, kept
 * here and returned to hand to holders and other verifiers (revocations.js
 * gossip). An anchor revoked before it synced is never uploaded (decision 8,
 * answer 6).
 * @param {string} anchorHash
 * @param {object} [opts]
 * @param {string} [opts.reason]
 * @returns {Promise<{ jws: string }>}
 */
export async function revokeGrant(anchorHash, { reason, authUrl } = {}) {
    const row = await getWalletToken(anchorHash)
    if (!row) throw new Error('revokeGrant: no such grant in this wallet')
    await acequiaReady()
    // Signed by the anchor's user (a kept identity for a grant from before a link)
    const signer = await ownIdentityFor(anchorUser(row.anchorJwt)) || { userId: getUserId(), userKeys: getUserKeys() }
    const keys = signer.userKeys
    if (!keys) throw new Error('revokeGrant: no user keys in this browser')
    const kid = await jose.calculateJwkThumbprint(await jose.exportJWK(keys.publicKey))
    const jws = await createRevocation({ revokes: row.anchorHash, signingKey: keys.privateKey, kid, sub: signer.userId, reason })

    await updateWalletToken(row.anchorHash, {
        status: 'revoked',
        revokedAt: new Date().toISOString(),
        revocationJws: jws,
        sync: row.sync === 'pending' ? 'dropped' : row.sync,
    })
    // Keep it, verified, beside the server's and the peers' (offline: the
    // anchor is in the wallet and the key is this browser's)
    const url = authUrlOf(authUrl)
    await storeRevocation(jws, {
        resolvePublicKey: createCombinedKeyResolver(url),
        resolveLink: createLinkResolver(url),
    }).catch(() => {})
    return { jws }
}

// ---------------------------------------------------------------------------
// Sync (decision 8, item 5): the server is where grants go, not where they
// become valid
// ---------------------------------------------------------------------------

const NEEDS_UPLOAD = new Set(['pending', 'leaf-pending'])

// ---------------------------------------------------------------------------
// Revocations learned from elsewhere
// ---------------------------------------------------------------------------

const _revokedElsewhere = new Set()
// Rows found already marked revoked, told once per page (see learnRevoked)
const _toldAlreadyRevoked = new Set()

/**
 * Listen for grants minted here that turned out to be revoked elsewhere (the
 * server's list, seen when verifying a presented grant or syncing).
 * @param {(anchorHash: string, row: object) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onGrantRevokedElsewhere(fn) {
    _revokedElsewhere.add(fn)
    return () => _revokedElsewhere.delete(fn)
}

/**
 * Mark a grant minted here revoked, because the server has it revoked. No
 * revocation of our own is made: the server's is the one in force, so there
 * is nothing to upload (sync skips a revoked row without `revocationJws`).
 * @returns {Promise<boolean>} whether it changed
 */
async function learnRevoked(anchorHash, via) {
    const row = await getWalletToken(anchorHash).catch(() => null)
    if (!row || row.origin !== 'minted') return false
    // Already marked revoked: by another tab (the wallet is the browser's),
    // whose page had no grant manager for this app to hear it. The listeners
    // here are told once anyway: a grant manager whose record was never
    // revoked kept handing the holder a grant every route refused (the
    // observatory CameraMap's thumbnails, 2026-10-22; issues ledger item 17)
    if (row.status === 'revoked') {
        if (!_toldAlreadyRevoked.has(anchorHash)) {
            _toldAlreadyRevoked.add(anchorHash)
            for (const fn of _revokedElsewhere) {
                try { fn(anchorHash, row) } catch { /* a listener's problem */ }
            }
        }
        return false
    }
    await updateWalletToken(anchorHash, {
        status: 'revoked',
        revokedAt: new Date().toISOString(),
        revokedElsewhere: via,
        sync: NEEDS_UPLOAD.has(row.sync) ? 'dropped' : row.sync,
    })
    console.log(`[grants] ${anchorHash.slice(0, 12)}… (${row.name || row.aud || 'a grant'}) is revoked on the server: marked revoked here (${via})`)
    for (const fn of _revokedElsewhere) {
        try { fn(anchorHash, row) } catch { /* a listener's problem */ }
    }
    return true
}

/**
 * Whether a grant minted here is marked revoked in this browser's wallet.
 * @param {string} anchorHash
 * @returns {Promise<boolean>}
 */
export async function isMarkedRevoked(anchorHash) {
    if (!anchorHash) return false
    const row = await getWalletToken(anchorHash).catch(() => null)
    return !!row && row.origin === 'minted' && row.status === 'revoked'
}

/** Whether this browser's user is registered and approved here; else why not. */
async function approvedHere(url) {
    let resp
    try {
        resp = await fetch(`${url}/users/${encodeURIComponent(getUserId())}`)
    } catch {
        return { ok: false, why: 'offline' }
    }
    if (resp.status === 404) return { ok: false, why: 'not-registered' }
    if (!resp.ok) return { ok: false, why: `user lookup ${resp.status}` }
    const role = (await resp.json().catch(() => ({})))?.user?.role
    // A pending account waits for the owner's approval (answer 5)
    if (!role || role === 'pending') return { ok: false, why: role ? 'pending-approval' : 'unknown-role' }
    return { ok: true }
}

/**
 * Grants this browser holds that the server doesn't have yet (or revocations
 * of ones it has). A grant rooted in an identity this browser had before
 * linking to the account waits until the server holds that identity as the
 * account's alias (identities.js `aliasAttested`): until then the server
 * can't tell the grant is the account's.
 */
async function syncWork() {
    const rows = await listWalletTokens({ appGrants: true }).catch(() => [])
    const current = getUserId()
    const out = []
    for (const r of rows) {
        if (r.origin !== 'minted') continue
        const due = (r.status !== 'revoked' && NEEDS_UPLOAD.has(r.sync)) ||
            (r.status === 'revoked' && (r.sync === 'synced' || r.sync === 'leaf-pending') && r.revocationJws && !r.revocationSynced)
        if (!due) continue
        const root = anchorUser(r.anchorJwt)
        if (root && root !== current) {
            const kept = await ownIdentityFor(root)
            if (!kept?.aliasAttested) continue
        }
        out.push(r)
    }
    return out
}

/**
 * Upload what this browser minted, if it is signed in to an approved
 * account: each pending grant's anchor and leaf (the leaf under its client
 * id, which becomes its short code — the server binds a fresh id to the first
 * store and never recycles one), a renewed grant's new leaf, and the signed
 * revocation of a grant revoked after it was uploaded. A grant revoked before
 * it uploaded is never sent (answer 6). Then the account's device keys are
 * kept (answer 4). Nothing is lost by failing: a row stays pending.
 *
 * @param {object} [opts]
 * @param {string} [opts.authUrl]
 * @returns {Promise<{ anchors: number, leaves: number, revocations: number, keys?: number, failed: object[], skipped?: string }>}
 */
export async function syncGrants({ authUrl } = {}) {
    await acequiaReady()
    const url = authUrlOf(authUrl)
    const done = { anchors: 0, leaves: 0, revocations: 0, failed: [] }
    const approved = await approvedHere(url)
    if (!approved.ok) return { ...done, skipped: approved.why, waiting: (await syncWork()).length }
    // Identities this browser had before linking to the account: attested as
    // its aliases first, so their grants are in the work below
    const aliases = await attestAliases({ authUrl: url }).catch(() => null)
    if (aliases?.attested?.length) done.aliases = aliases.attested.length
    const work = await syncWork()

    for (const row of work) {
        try {
            if (row.status === 'revoked') {
                const resp = await fetch(`${url}/revocations/${row.anchorHash}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/jwt' },
                    body: row.revocationJws,
                })
                if (resp.status === 401) return { ...done, skipped: 'no-session', waiting: work.length }
                if (!resp.ok) throw new Error(`revocation ${resp.status}`)
                await updateWalletToken(row.anchorHash, { revocationSynced: new Date().toISOString() })
                done.revocations++
                continue
            }
            if (row.sync === 'pending') {
                await storeAnchor(url, null, row.anchorJwt)
                done.anchors++
            }
            let leaf
            try {
                leaf = await storeChainToken(url, null, row.leafJwt, { reuseId: row.leafId })
            } catch (err) {
                // The server's terminal answers (410), never worth a retry —
                // retried at every sync, one grant made 123 uploads a day:
                //  - its lineage is revoked there: the grant is dead
                //  - its code is tombstoned (the grant may be fine): a fresh
                //    code, once, rather than the dead one again
                const revoked = async (e) => {
                    await learnRevoked(row.anchorHash, 'sync')
                    done.failed.push({ anchorHash: row.anchorHash, error: e.message, terminal: 'revoked' })
                }
                if (/revoked lineage/.test(err.message || '')) { await revoked(err); continue }
                if (!/tombstoned/.test(err.message || '') || !row.leafId) throw err
                try {
                    leaf = await storeChainToken(url, null, row.leafJwt, {})
                } catch (again) {
                    // The fresh code refused as revoked: the grant is dead after all
                    if (/revoked lineage/.test(again.message || '')) { await revoked(again); continue }
                    throw again
                }
            }
            await updateWalletToken(row.anchorHash, {
                sync: 'synced',
                syncedAt: new Date().toISOString(),
                leafId: leaf?.id || row.leafId,
            })
            done.leaves++
        } catch (err) {
            if (/Authentication required|401/.test(err.message || '')) return { ...done, skipped: 'no-session', waiting: work.length }
            done.failed.push({ anchorHash: row.anchorHash, error: err.message })
        }
    }
    const keys = await cacheAccountKeys(url).catch(() => ({ cached: 0 }))
    return { ...done, keys: keys.cached }
}

let _syncTimer = null
let _syncRunning = null

/**
 * Keep this browser's grants syncing: shortly after start, whenever the
 * browser comes back online, and every interval. Idempotent; a run with
 * nothing to send asks the server nothing.
 * @param {object} [opts]
 * @param {string} [opts.authUrl]
 * @param {number} [opts.intervalMs=600000]
 * @param {(result: object) => void} [opts.onResult]
 */
export function startGrantSync({ authUrl, intervalMs = 10 * 60 * 1000, onResult } = {}) {
    if (_syncTimer) return
    const run = async () => {
        if (_syncRunning) return _syncRunning
        _syncRunning = (async () => {
            try {
                if (!(await syncWork()).length) return null
                const result = await syncGrants({ authUrl })
                if (result.anchors || result.leaves || result.revocations || result.failed.length) {
                    console.log(`[grants] synced: ${result.anchors} anchors, ${result.leaves} leaves, ${result.revocations} revocations${result.failed.length ? `, ${result.failed.length} failed` : ''}`)
                }
                onResult?.(result)
                return result
            } catch (err) {
                console.warn('[grants] sync failed:', err.message)
                return null
            } finally {
                _syncRunning = null
            }
        })()
        return _syncRunning
    }
    _syncTimer = setInterval(run, intervalMs)
    setTimeout(run, 5000)
    if (typeof addEventListener === 'function') addEventListener('online', run)
}

/** Stop the background sync. */
export function stopGrantSync() {
    if (_syncTimer) clearInterval(_syncTimer)
    _syncTimer = null
}
